import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutateFetchMyBrands } from '@retail/auth/data-access';
import { AppTenant, Brand, BrandDTO } from '@retail/auth/types';
import { useSuspenseQuery } from '@tanstack/react-query';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { brandLogo } from '@retail/utils';
import {
  BrandsProvider,
  MpeTenantHeader,
  selectedBrandKey,
} from '@retail/app/stores/selected-context';

const setInitialBrand = (brands: Brand[]) => {
  if (!brands.length)
    throw new Error('Expected at least one brand. Should not reach this point without a brand.');

  const storedBrandKey = localStorage.getItem(selectedBrandKey);
  const selectedBrand = brands.find(({ brandId }) => brandId === storedBrandKey);

  if (selectedBrand) return selectedBrand;

  const firstBrand = brands[0];
  localStorage.setItem(selectedBrandKey, firstBrand.brandId);

  return firstBrand;
};

interface Props extends PropsWithChildren {
  appTenants: AppTenant[];
}

export const BrandsProviderImpl: React.FC<Props> = ({ children, appTenants }) => {
  const navigate = useNavigate();

  const { mutateAsync: fetchMySwedishBrands } = useMutateFetchMyBrands({
    headers: { [MpeTenantHeader]: AppTenant.SWEDEN.tenantAppKey },
  });
  const { mutateAsync: fetchMyNorwegianBrands } = useMutateFetchMyBrands({
    headers: { [MpeTenantHeader]: AppTenant.NORWAY.tenantAppKey },
  });

  const { data: availableBrands } = useSuspenseQuery({
    queryKey: ['availableBrands', appTenants],
    queryFn: async () => {
      const testCalls: Promise<BrandDTO[]>[] = [];

      if (appTenants.includes(AppTenant.SWEDEN)) {
        testCalls.push(fetchMySwedishBrands({}));
      } else {
        testCalls.push(Promise.resolve([]));
      }

      if (appTenants.includes(AppTenant.NORWAY)) {
        testCalls.push(fetchMyNorwegianBrands({}));
      } else {
        testCalls.push(Promise.resolve([]));
      }

      const results = await Promise.all(testCalls);
      const swedishBrands = results[0];
      const norwegianBrands = results[1];

      return [
        ...swedishBrands.map((dto) => Brand.fromDto(dto, AppTenant.SWEDEN)),
        ...norwegianBrands.map((dto) => Brand.fromDto(dto, AppTenant.NORWAY)),
      ];
    },
  });

  const [selectedBrand, setSelectedBrand] = useState<Brand>(() => setInitialBrand(availableBrands));

  const availableBrandsForChosenTenant = useMemo(
    () => availableBrands.filter((brand) => brand.tenant === selectedBrand.tenant),
    [availableBrands]
  );

  const switchBrand = useCallback(
    (brand: Brand) => {
      if (brand.brandId !== selectedBrand.brandId) {
        localStorage.setItem(selectedBrandKey, brand.brandId);

        setSelectedBrand(brand);
        navigate(mgPrisRoutes.root.fullRoutePath);
        window.location.reload();
      }
    },
    [navigate, selectedBrand.brandId]
  );

  return (
    <BrandsProvider
      value={{
        availableBrands,
        availableBrandsForChosenTenant,
        selectedBrand,
        selectedBrandLogos: brandLogo(selectedBrand.brandId),
        switchBrand,
      }}
    >
      {children}
    </BrandsProvider>
  );
};
